<template>
  <div class="flex items-center gap-6" :class="{ 'mb-20': hasErrors }">
    <!-- Partner‑field select -->
    <div class="relative flex-1 min-w-0">
      <BittsSelect
        placeholder="Select partner field"
        class="w-full"
        option-type="company"
        :model-value="mapping?.partner_source_field_id"
        :options="options"
        :filter-function="partnerFilterFunc"
        :error="hasErrors && mapping?.partner_source_field_id === null"
        :disabled
        size="medium"
        @change="
          (val: number | null) =>
            emit('changed:mapping', {
              index,
              mapping: {
                ...mapping,
                partner_source_field_id: val,
              },
            })
        "
      />
      <FieldMappingError
        field="partnerField"
        v-if="hasErrors && mapping?.partner_source_field_id === null"
      />
    </div>

    <FontAwesomeIcon
      :icon="['fak', 'arrow-right']"
      class="self-center text-neutral-accent"
    />

    <!-- CRM‑field select -->
    <div class="relative flex-1 min-w-0">
      <BittsSelect
        placeholder="Select CRM field"
        class="w-full"
        :model-value="mapping?.source_field_id"
        :options="crmFields"
        :error="hasErrors && mapping?.source_field_id === null"
        :disabled
        size="medium"
        @change="
          (val: number | null) =>
            emit('changed:mapping', {
              index,
              mapping: {
                ...mapping,
                source_field_id: val,
              },
            })
        "
      />
      <FieldMappingError
        field="crmField"
        v-if="hasErrors && mapping?.source_field_id === null"
      />
    </div>

    <BittsButton
      size="small"
      type="neutral"
      variant="ghost"
      class="delete-mapping-button"
      :center-icon="['fak', 'delete']"
      aria-label="Delete mapping"
      :disabled="
        disabled ||
        (!mapping.partner_source_field_id &&
          !mapping.source_field_id &&
          index === 0)
      "
      @click="emit('deleted:mapping', index)"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsSelect } from '@crossbeam/bitts';

import { computed } from 'vue';

import { Partner } from '@/types/partners';

import FieldMappingError from './FieldMappingError.vue';

type Option = {
  label: string;
  value: number | null;
};

type PartnerOption = Option & Partner;

export type Mapping = {
  id: string;
  source_field_id: number | null;
  partner_organization_uuid: string | null;
  partner_source_field_id: number | null;
  empty: boolean;
};

const {
  index,
  crmFields = [],
  partnerFields = [],
  mapping,
  disabled = false,
} = defineProps<{
  index: number;
  crmFields: Option[];
  partnerFields: PartnerOption[];
  mapping: Mapping;
  disabled: boolean;
}>();

const emit = defineEmits<{
  'deleted:mapping': [index: number];
  'changed:mapping': [
    mapping: {
      mapping: Mapping;
      index: number;
    },
  ];
}>();

const trimVal = (val: string) => val.trim().toLowerCase();

const options = computed(() =>
  partnerFields.map((field) => ({
    ...field,
    tooltipText: `${field.name} - ${field.label}`,
  })),
);

const hasErrors = computed(
  () =>
    !mapping.empty &&
    (mapping?.partner_source_field_id === null ||
      mapping.source_field_id === null),
);

function partnerFilterFunc(
  val: string,
  option: PartnerOption,
  options: PartnerOption[],
): boolean {
  const trimmedVal = trimVal(val);
  const foundOption = options.find((o) => o.value === option.value);

  if (!foundOption) return false;

  const haystack = trimVal(foundOption.name) + trimVal(foundOption.label);

  return haystack.includes(trimmedVal);
}
</script>
